import { Badge, Flex, Text } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import TableComponent from '../components/ui-components/TableComponent';
import ViewSensor from '../components/ui-components/ViewSensor';
import SearchFilter from '../components/ui-components/SearchFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import AddSensorPopup from '../components/ui-components/AddSensorPopup';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import axiosInstance from '../config/axiosConfig';
import debounce from 'lodash/debounce';
import { highlightSearchTerm } from '../utils/helpers';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SensorAdminPage = () => {
  const [sensors, setSensors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const pageSize = 10;

  const showToast = (message, type = 'success') => {
    if (type === 'error') toast.error(message);
    else toast.success(message);
  };

  const fetchSensors = useCallback(async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
    try {
      const response = await axiosInstance.get('/sensor/get-institution-sensors', {
        params: {
          pageNumber: page,
          pageSize,
          searchQuery: query.length >= 3 ? query : null,
          isActive: activeOnly ? true : null,
        },
      });
      if (response.data && response.data.result) {
        const sensorData = response.data.result.sensorList.map((item, index) => ({
          serialNumber: (page - 1) * pageSize + index + 1,
          token: item.token,
          isin: item.isin,
          isvn: item.isvn,
          isActive: item.isActive,
        }));
        setSensors(sensorData);
        setTotalCount(response.data.result.totalCount || 0);
      } else {
        setSensors([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching sensors:', error.response ? error.response.data : error.message);
      setSensors([]);
      setTotalCount(0);
    }
  }, [searchQuery, showActiveOnly]);

  const debouncedFetch = useCallback(debounce((page, query, activeOnly) => {
    fetchSensors(page, query, activeOnly);
  }, 300), [fetchSensors]);

  useEffect(() => {
    debouncedFetch(currentPage, searchQuery, showActiveOnly);
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  const handleSearchChange = () => { setCurrentPage(1); };
  const handleActiveToggle = () => { setCurrentPage(1); };
  const handleSensorUpdated = async (change = null) => {
    if (change?.deleted) {
      setSensors((items) => items.filter((item) => item.token !== change.token));
      setTotalCount((count) => Math.max(0, count - 1));
    } else if (typeof change?.isActive === 'boolean') {
      setSensors((items) =>
        items.map((item) =>
          item.token === change.token ? { ...item, isActive: change.isActive } : item
        )
      );
    }
    await fetchSensors(currentPage, searchQuery, showActiveOnly);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const pageStartIndex = (currentPage - 1) * pageSize;

  const columns = [
    {
      title: '#',
      key: 'serialNumber',
      render: (value) => <Text size={2}>{value}</Text>,
    },
    {
      title: 'ISIN',
      key: 'isin',
      render: (value) => <Text size={2}>{highlightSearchTerm(value, searchQuery)}</Text>,
    },
    {
      title: 'ISVN',
      key: 'isvn',
      render: (value) => <Text size={2}>{highlightSearchTerm(value, searchQuery)}</Text>,
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (value) => (
        <Badge color={value ? 'green' : 'tomato'}>
          <MdOutlineVerifiedUser color={value ? 'green' : 'red'} />
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Flex gap={'2'} align={'center'}>
          <ViewSensor
            initialValues={row}
            onSensorUpdated={handleSensorUpdated}
            showToast={showToast}
            isAdmin={true}
          />
        </Flex>
      ),
    },
  ];

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search sensors (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <AddSensorPopup
            onSensorAssigned={() => fetchSensors(1, searchQuery, showActiveOnly)}
            showToast={showToast}
          />
        </Flex>
        <TableComponent dataStructure={sensors} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {pageStartIndex + 1} – {Math.min(pageStartIndex + pageSize, totalCount)} of {totalCount} sensors
          </Text>
        )}
        {totalCount === 0 && !sensors.length && (
          <Flex justify="center" align="center" style={{ minHeight: '120px' }}>
            <Text size="3" color="gray">No sensors found</Text>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default SensorAdminPage;
